import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { type RolePermissionsActivityLogFiltersType } from "#src/batteries-included-components/FilterAreas/RolePermissionsActivityLogFilterAreas.tsx/RolePermissionsActivityLogFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import {
  useExportActivityLogs,
  useListActivityLogs,
} from "#src/components/hooks/adapters/useActivityLogs";
import { useListRoles } from "#src/components/hooks/adapters/useRoles";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { getPermissionDisplayLabel } from "#src/contexts/AuthenticatedContext.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToRoleDetailPage } from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail";
import {
  ArrayDataDisplay,
  Button,
  DataTable,
  DataTablePanel,
  type HeaderType,
  Icon,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  ActivityAction,
  ActivityAdapter,
  ActivityExportResource,
  ActivityResource,
  ActivitySchema,
  type ActivityType,
  type PermissionStatementType,
  type RoleType,
  SortDirection,
  UserTypeEnum,
  type UserWithGroupsType,
} from "@validereinc/domain";
import { isEmpty } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";

const activityModelKeys = ActivitySchema.keyof().Enum;

type RolePermissionsActivityLogBeforeAfter = {
  permissions?: PermissionStatementType[];
};

export const RolePermissionsActivityLogTablePanel = ({
  viewConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey" | "tableStateStorageKey">) => {
  const [viewFilters] = useStickyState<RolePermissionsActivityLogFiltersType>(
    {} as RolePermissionsActivityLogFiltersType,
    viewConfigStorageKey
  );

  const { resource_id, logged_by, logged_at } = viewFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "timestamp",
      sortDirection: SortDirection.DESCENDING,
      pageSize: 25,
    },
    undefined,
    tableStateStorageKey
  );
  const [selectedActivities, setSelectedActivities] = useState<
    Record<string, ActivityType>
  >({});

  const viewFiltersToApply = {
    ...(resource_id && { resource_id }),
    ...(logged_by && ({ author_id: logged_by } as any)),
    ...(logged_at && ({ timestamp: logged_at } as any)),
  };

  const activitiesQueryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...viewFiltersToApply,
      action: ActivityAction.PERMISSION_UPDATE,
    },
    meta: { resourceType: ActivityResource.ROLE },
  } satisfies Parameters<typeof ActivityAdapter.getList>[0];

  const { data: activitiesData, isLoading: areActivityLogsLoading } =
    useListActivityLogs(activitiesQueryPayload);

  const activities = activitiesData?.data;

  const processActivityPermissions = useMemo(() => {
    const uniqueAuthorIds = new Set<string>();
    const uniqueRoleIds = new Set<string>();
    const beforePermissions: Record<string, Set<string>> = {};
    const afterPermissions: Record<string, Set<string>> = {};

    activities?.forEach((activity) => {
      uniqueAuthorIds.add(activity.author_id);
      uniqueRoleIds.add(activity.resource_id);

      const activityBefore =
        activity.before as RolePermissionsActivityLogBeforeAfter;
      const activityAfter =
        activity.after as RolePermissionsActivityLogBeforeAfter;
      beforePermissions[activity.id] = new Set(
        activityBefore?.permissions?.map((p) =>
          getPermissionDisplayLabel(p.name)
        )
      );
      afterPermissions[activity.id] = new Set(
        activityAfter?.permissions?.map((p) =>
          getPermissionDisplayLabel(p.name)
        )
      );
    });

    const uniqueAuthorIdsArray = Array.from(uniqueAuthorIds);
    const uniqueRoleIdsArray = Array.from(uniqueRoleIds);

    return {
      uniqueAuthorIdsArray,
      uniqueRoleIdsArray,
      beforePermissions,
      afterPermissions,
    };
  }, [activities]);

  const {
    uniqueAuthorIdsArray,
    uniqueRoleIdsArray,
    beforePermissions,
    afterPermissions,
  } = processActivityPermissions;

  const { data: usersQueryData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueAuthorIdsArray,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    {
      enabled: Boolean(uniqueAuthorIdsArray.length),
    }
  );
  const users = usersQueryData?.data ?? [];
  const authorIdsToUserDetailsMap = useMemo(() => {
    return Object.fromEntries(users.map((user) => [user.id, user])) as Record<
      string,
      UserWithGroupsType
    >;
  }, [users]);

  const { data: rolesQueryData, isLoading: isLoadingRoles } = useListRoles(
    { filters: { id: uniqueRoleIdsArray } },
    {
      enabled: Boolean(uniqueRoleIdsArray.length),
    }
  );
  const roles = rolesQueryData?.data ?? [];
  const roleIdsToRoleDetailsMap = useMemo(() => {
    return Object.fromEntries(roles.map((role) => [role.id, role])) as Record<
      string,
      RoleType
    >;
  }, [roles]);

  const exportActivityLogPayload = {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...(!isEmpty(selectedActivities)
        ? { id: Object.keys(selectedActivities) }
        : {}),
      ...viewFiltersToApply,
    },
    meta: { resourceType: ActivityExportResource.ROLE, period: "" },
  } satisfies Parameters<typeof ActivityAdapter.exportList>[0];

  const { mutate: handleExport, isLoading: isExporting } =
    useExportActivityLogs(exportActivityLogPayload);

  const isLoading =
    areActivityLogsLoading ||
    (Boolean(uniqueAuthorIdsArray.length) && isLoadingUsers) ||
    (Boolean(uniqueRoleIdsArray.length) && isLoadingRoles);

  const headers = useMemo<Array<HeaderType<ActivityType>>>(
    () => [
      {
        key: activityModelKeys.action,
        label: "Type",
        renderComponent: () => {
          return (
            <Pill
              variant="default"
              isLoading={isLoading}
            >
              Permission Modified
            </Pill>
          );
        },
      },
      {
        key: activityModelKeys.resource_id,
        label: "Role",
        renderComponent: ({ item }) => {
          const roleId = item.resource_id;
          const roleName = roleIdsToRoleDetailsMap[roleId]?.name;
          return roleId && roleName ? (
            <RoutingLink to={linkToRoleDetailPage(roleId)}>
              {roleName}
            </RoutingLink>
          ) : (
            "-"
          );
        },
      },
      {
        key: "permissions_added",
        label: "Permissions Added",
        renderComponent: ({ item }) => {
          const permissionsAdded = Array.from(
            afterPermissions[item.id].difference(beforePermissions[item.id]) ??
              new Set<string>()
          );

          return <ArrayDataDisplay value={permissionsAdded ?? []} />;
        },
      },
      {
        key: "permissions_removed",
        label: "Permissions Removed",
        renderComponent: ({ item }) => {
          const permissionsRemoved = Array.from(
            beforePermissions[item.id].difference(afterPermissions[item.id]) ??
              new Set<string>()
          );
          return <ArrayDataDisplay value={permissionsRemoved ?? []} />;
        },
      },
      {
        key: activityModelKeys.author_id,
        label: "Logged By",
        renderComponent: ({ item }) => {
          const authorId = item.author_id;
          const authorName = authorIdsToUserDetailsMap[authorId]?.name;
          return authorId && authorName ? (
            <UserDataDisplay
              id={authorId}
              name={authorName}
              isLoading={isLoading}
            />
          ) : (
            "-"
          );
        },
      },
      {
        key: activityModelKeys.timestamp,
        label: "Logged At",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item[activityModelKeys.timestamp]}
            withTime
            isLoading={isLoading}
          />
        ),
      },
    ],
    [
      roleIdsToRoleDetailsMap,
      authorIdsToUserDetailsMap,
      afterPermissions,
      beforePermissions,
    ]
  );

  const actionRow = [
    <IsFeatureAvailable
      key="export-roles-permissions-activity-log"
      featureFlagQuery={{ $and: ["reports:write", "reports:read"] }}
      fallbackChildren={null}
    >
      <PermissionAwareExportButton
        onClick={handleExport}
        isExporting={isExporting}
      />
    </IsFeatureAvailable>,
  ];

  return (
    <DataTablePanel
      panelProps={{
        title: "Change Log",
        actionRow,
      }}
      dataTableProps={{
        headers,
        items: activities ?? [],
        isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: activitiesData?.total_entries,
          pageSizeText: "logs per page",
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        selected: selectedActivities,
        onSelectionChange: setSelectedActivities,
        getItemId: (item) => item.id,
        getItemBulkActions: ({ buttonProps }) => [
          <IsFeatureAvailable
            key="export"
            featureFlagQuery={{ $and: ["reports:write", "reports:read"] }}
            fallbackChildren={null}
          >
            <Button
              {...buttonProps}
              variant="outline"
              slotLeft={<Icon variant="arrow-square-out" />}
              isLoading={isExporting}
              onClick={() => handleExport()}
            >
              Export
            </Button>
          </IsFeatureAvailable>,
        ],
      }}
    />
  );
};
